import { 
  MDBCard, 
  MDBCardBody, 
  MDBCol, 
  MDBContainer, 
  MDBRow, 
  MDBSpinner, 
  MDBAlert,
  MDBBtn,
  MDBIcon
} from "mdb-react-ui-kit";
import React, { useContext, useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { DevContext } from "../../../../App.jsx";
import { FormProvider, useFormContext } from "./components/FormProvider.jsx";
import StepRenderer from "./components/StepRenderer.jsx";
import { useValidation, ValidationProvider } from "./components/ValidationProvider.jsx";
import { StepsConfigProvider } from "./contexts/StepsConfigContext.jsx";
import { useStepNavigation } from "./hooks/useStepNavigation.js";
import { useLectureAPI } from "./hooks/useLectureAPI.js";
import { useCompanyData } from "./hooks/useCompanyData.js";
import { useIndustry } from "../../../../utils/industryContextHook";
import { transformDBToFormFormat, transformFormToDBFormat } from "./utils/lectureDataTransform.js";
import { StepperNavigation } from "./stepper/index.ts";
import { createStepsConfig } from "./steps/stepsConfig.js";
import { updateStepsArray } from "./utils/stepHelpers.js";
import { lounges26Schema } from "./validation/lectures.js";
import { getLocalizedText } from "../../../../utils/localizedText.js";

// Main component with form context
function LectureRegistrationContent({ steps, setSteps, companyId, eventId, lectureId, onSaveLecture, onCloseRequest, isModal = false }) {
  const { t } = useTranslation();
  const { formData, markStepCompleted, markStepUncompleted, loadLectureData, hasUnsavedChanges } = useFormContext();
  const { isFormReadyToSave, getValidationSummary, getStepValidation, isStepComplete } = useValidation();
  const { isDevMode } = useContext(DevContext);
  const { currentIndustry } = useIndustry();
  
  // Use the lecture API hook
  const { fetchLecture, createLecture, updateLecture, isLoading: isSavingAPI, error: lectureError } = useLectureAPI();
  const { employees } = useCompanyData(companyId);
  
  // State for loading
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  

  // Navigation now uses context
  const { currentStep, navigateToStep, canNavigateToStep } = useStepNavigation();
  
  // State for highlighting incomplete steps
  const [highlightIncompleteSteps, setHighlightIncompleteSteps] = React.useState(false);
  
  // Memoized change detection for performance
  const hasChanges = React.useMemo(() => {
    return hasUnsavedChanges?.() || false;
  }, [hasUnsavedChanges, formData]); // Re-calculate when form data changes

  // Check if we're in edit mode
  const isEditMode = lectureId && lectureId !== 'new';

  // Track if lecture data has been loaded
  const [isLectureLoaded, setIsLectureLoaded] = React.useState(false);
  // Track the lecture ID that we've navigated for
  const [navigatedForLectureId, setNavigatedForLectureId] = React.useState(null);
  
  // Reset navigation tracking when lectureId changes
  React.useEffect(() => {
    setNavigatedForLectureId(null);
    setIsLectureLoaded(false);
  }, [lectureId]);

  // Load lecture data if in edit mode
  React.useEffect(() => {
    // Prevent duplicate loading
    if (!isEditMode) return;
    
    let isCancelled = false;
    
    const loadExistingLecture = async () => {
      console.log("Loading lecture for editing, ID:", lectureId);
      setIsLoading(true);
      setLoadError(null);
      setIsLectureLoaded(false);
      // Reset navigation tracking when starting a new load
      setNavigatedForLectureId(null);
      
      try {
        // Fetch lecture from API
        const dbLectureData = await fetchLecture(lectureId);
        
        if (isCancelled) return;
        
        if (dbLectureData) {
          // Transform DB format to form format
          const formLectureData = transformDBToFormFormat(dbLectureData, employees);
          console.log("Transformed lecture data for form:", formLectureData);
          
          // Load into form
          loadLectureData(formLectureData);
          // Mark that lecture has been loaded
          setIsLectureLoaded(true);
        } else {
          throw new Error('Lecture not found');
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error loading lecture data:", error);
          setLoadError(error.message || t('Failed to load lecture data'));
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadExistingLecture();
    
    return () => {
      isCancelled = true;
    };
  }, [lectureId, isEditMode, fetchLecture, loadLectureData, employees, t]);

  // Auto-complete steps based on schema-driven data validation
  React.useEffect(() => {
    // Check all steps for completion using the centralized schema-based validation
    const allStepIds = [
      'information', 'general_conditions', 'lecture_type', 'conditions', 
      'language', 'title', 'subjects', 'description', 'topics', 
      'speakers', 'image', 'handout', 'presentation'
    ];

    allStepIds.forEach(stepId => {
      const shouldBeCompleted = isStepComplete(stepId);
      const isCurrentlyCompleted = formData.completedSteps.includes(stepId);

      if (shouldBeCompleted && !isCurrentlyCompleted) {
        markStepCompleted(stepId);
      } else if (!shouldBeCompleted && isCurrentlyCompleted) {
        markStepUncompleted(stepId);
      }
    });
  }, [formData, isStepComplete, markStepCompleted, markStepUncompleted]);

  // Update steps based on form state and validation
  React.useEffect(() => {
    setSteps((prevSteps) => updateStepsArray(prevSteps, formData, currentStep, formData.completedSteps, getStepValidation));
  }, [currentStep, formData.completedSteps, formData, getStepValidation, setSteps]);

  // Navigate to review step in edit mode after lecture is loaded and steps are validated
  React.useEffect(() => {
    const shouldNavigate = isEditMode && 
                          isLectureLoaded && 
                          !isLoading && 
                          lectureId !== navigatedForLectureId &&
                          formData.currentStep !== 'review';
    
    console.log("Navigation attempt:", { 
      isEditMode, 
      isLectureLoaded, 
      isLoading, 
      lectureId,
      navigatedForLectureId,
      shouldNavigate,
      canNavigate: canNavigateToStep('review'),
      completedSteps: formData.completedSteps,
      currentStep: formData.currentStep
    });
    
    if (shouldNavigate) {
      // Small delay to ensure all step completion states are updated
      const timer = setTimeout(() => {
        console.log("Timer executing - checking navigation conditions:", {
          canNavigate: canNavigateToStep('review'),
          completedSteps: formData.completedSteps,
          validationSummary: getValidationSummary()
        });
        
        if (canNavigateToStep('review')) {
          console.log("Navigating to review step for editing");
          navigateToStep('review');
          setNavigatedForLectureId(lectureId);
        } else {
          console.log("Cannot navigate to review step - some required steps are not completed");
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isEditMode, isLectureLoaded, isLoading, lectureId, navigatedForLectureId, canNavigateToStep, navigateToStep, formData.completedSteps, formData.currentStep, getValidationSummary]);

  // Activate highlight mode when on review step or in edit mode
  React.useEffect(() => {
    if (currentStep === 'review') {
      setHighlightIncompleteSteps(true);
    }
    // Also activate in edit mode
    if (isEditMode && isLectureLoaded) {
      setHighlightIncompleteSteps(true);
    }
  }, [currentStep, isEditMode, isLectureLoaded]);

  const handleStepChange = (selectedStep) => {
    console.log("Step/Substep selected:", selectedStep);

    let targetStepId;

    if (selectedStep.parentStepId) {
      // It's a substep - use the substep ID
      targetStepId = selectedStep.id;
    } else if (selectedStep.substeps && selectedStep.substeps.length > 0) {
      // It's a main step with substeps - navigate to first substep
      targetStepId = selectedStep.substeps[0].id;
    } else {
      // It's a main step without substeps
      targetStepId = selectedStep.id;
    }

    console.log("Navigating to:", targetStepId);
    
    // Check if navigation is allowed before attempting
    if (!canNavigateToStep(targetStepId)) {
      console.log("Navigation blocked - step requires completion of prerequisites");
      return;
    }
    
    const success = navigateToStep(targetStepId);
    console.log("Navigation success:", success);
  };

  const handleStepComplete = (stepId) => {
    console.log("Step completed:", stepId);

    // Log validation state when step is completed
    const validationSummary = getValidationSummary();
    console.log("Validation summary on step completion:", validationSummary);
    console.log("Form ready to save:", isFormReadyToSave());
  };

  /**
   * Handle saving the lecture - this is passed to ReviewStep
   * The actual save operation happens here in the parent component
   */
  const handleSaveLecture = useCallback(async () => {
    try {
      // Transform form data to database format
      const transformedData = transformFormToDBFormat(formData, companyId, eventId, currentIndustry?.id);
      
      console.log('Saving lecture with data:', transformedData);
      
      let savedLecture;
      if (lectureId && lectureId !== 'new') {
        // Update existing lecture
        savedLecture = await updateLecture(lectureId, transformedData);
      } else {
        // Create new lecture
        savedLecture = await createLecture(transformedData);
      }
      
      console.log('Lecture saved successfully:', savedLecture);
      
      // Call the parent callback with the saved lecture data
      if (onSaveLecture) {
        onSaveLecture(savedLecture);
      }
      
      return savedLecture;
    } catch (error) {
      console.error('Error saving lecture:', error);
      throw error;
    }
  }, [formData, companyId, eventId, lectureId, createLecture, updateLecture, onSaveLecture]);


  /**
   * Handle save as draft and close
   */
  const handleSaveAsDraft = useCallback(async () => {
    try {
      // Transform form data to database format
      const transformedData = transformFormToDBFormat(formData, companyId, eventId, currentIndustry?.id);
      // Add draft flag
      transformedData.isDraft = true;
      
      console.log('Saving lecture as draft:', transformedData);
      
      let savedLecture;
      if (lectureId && lectureId !== 'new') {
        // Update existing lecture
        savedLecture = await updateLecture(lectureId, transformedData);
      } else {
        // Create new lecture
        savedLecture = await createLecture(transformedData);
      }
      
      console.log('Draft saved successfully:', savedLecture);
      
      // Close the modal
      if (onCloseRequest) {
        onCloseRequest();
      }
      
      // Call the parent callback with the saved lecture data
      if (onSaveLecture) {
        onSaveLecture({ ...savedLecture, isDraft: true });
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      // You might want to show an error message here
    }
  }, [formData, companyId, eventId, lectureId, createLecture, updateLecture, onCloseRequest, onSaveLecture]);

  /**
   * Handle discard changes and close
   */
  const handleDiscardChanges = useCallback(() => {
    if (onCloseRequest) {
      onCloseRequest();
    }
  }, [onCloseRequest]);


  // Show loading spinner if loading lecture data
  if (isLoading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "100vh" }}>
        <MDBSpinner style={{ width: '3rem', height: '3rem' }} className="mb-3">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
        <p className="text-muted">{t('Loading lecture data...')}</p>
      </div>
    );
  }

  // Show error if loading failed
  if (loadError) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "100vh" }}>
        <MDBAlert color="danger" className="mb-3">
          <h4 className="alert-heading">{t('Error')}</h4>
          <p>{loadError}</p>
          <hr />
          <p className="mb-0">{t('Please try again or contact support if the problem persists.')}</p>
        </MDBAlert>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column" style={{ height: "100vh" }}>
      {/* Add header with title and close buttons if in modal mode */}
      {isModal && onCloseRequest && (
        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom bg-white">
          <div>
            <h5 className="mb-0">
              {isEditMode 
                ? `${t('Edit lecture')}: ${getLocalizedText(formData.title) || t('Untitled Lecture')}` 
                : t('Create new lecture')
              }
            </h5>
          </div>
          <div className="d-flex me-4">
          {!hasChanges ? (
            // Show only close button when no changes
            <MDBBtn color="danger" outline onClick={handleDiscardChanges}>
              <MDBIcon fas icon="times" className="me-2" />
              {t('Close')}
            </MDBBtn>
          ) : (
            // Show discard and save draft buttons when changes exist
            <>
              <MDBBtn color="link" className="text-danger" onClick={handleDiscardChanges}>
                <MDBIcon fas icon="times" className="me-2" />
                {t('Close & Discard')}
              </MDBBtn>
              <MDBBtn color="success" outline onClick={handleSaveAsDraft}>
                <MDBIcon fas icon="save" className="me-2" />
                {t('Save Draft')}
              </MDBBtn>
            </>
          )}
          </div>
        </div>
      )}
      
      {/* Main content area */}
      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        {/* Navigation - fixed width, no scroll */}
        <div className="pe-5 border-end d-flex justify-content-center align-items-center" style={{ flexShrink: 0 }}>
          <StepperNavigation 
            steps={steps} 
            setSteps={setSteps} 
            onStepChange={handleStepChange} 
            activeStep={currentStep} 
            multiExpand={false} 
            size="xl" 
            canNavigateToStep={canNavigateToStep} 
            highlightIncomplete={highlightIncompleteSteps} 
          />
        </div>
        
        {/* Content area - scrollable */}
        <div className="flex-grow-1 px-4 py-4" style={{ overflow: "hidden" }}>
          <div className="h-100 overflow-auto">
            <StepRenderer 
              activeStep={currentStep} 
              onStepComplete={handleStepComplete} 
              onStepChange={handleStepChange} 
              companyId={companyId} 
              eventId={eventId} 
              lectureId={lectureId}
              onSaveLecture={handleSaveLecture}
              isSavingLecture={isSavingAPI}
            />
          </div>
        </div>
      </div>
      
      {isDevMode && (
        <div className="border-top bg-light p-3">
          <h5 className="text-danger mb-3">Debug Information (Dev Mode)</h5>
          <div className="row">
            <div className="col-md-6">
              <h6>Current Lecture Object:</h6>
              <pre className="bg-white p-3 rounded border" style={{ fontSize: "12px", maxHeight: "300px", overflow: "auto" }}>
                {JSON.stringify(formData, null, 2)}
              </pre>
            </div>
            <div className="col-md-6">
              <h6>Validation Results:</h6>
              <pre className="bg-white p-3 rounded border" style={{ fontSize: "12px", maxHeight: "300px", overflow: "auto" }}>
                {JSON.stringify(getValidationSummary(), null, 2)}
              </pre>
              <div className="mt-2">
                <strong>Form Ready to Save:</strong> <span className={isFormReadyToSave() ? "text-success" : "text-danger"}>{isFormReadyToSave() ? "Yes" : "No"}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Component that provides steps config context
function LectureRegistrationWithStepsConfig({ companyId, eventId, lectureId, onSaveLecture, onCloseRequest, isModal }) {
  const { t } = useTranslation();
  const [steps, setSteps] = React.useState(() => createStepsConfig(t));

  return (
    <StepsConfigProvider stepsConfig={steps}>
      <LectureRegistrationContent 
        steps={steps} 
        setSteps={setSteps} 
        companyId={companyId} 
        eventId={eventId} 
        lectureId={lectureId} 
        onSaveLecture={onSaveLecture}
        onCloseRequest={onCloseRequest}
        isModal={isModal}
      />
    </StepsConfigProvider>
  );
}

// Main export component with FormProvider wrapper
export default function Index({ companyId, eventId, lectureId, onSaveLecture, onCloseRequest, isModal = false }) {
  return (
    <FormProvider>
      <ValidationProvider eventConfig={lounges26Schema}>
        <LectureRegistrationWithStepsConfig 
          companyId={companyId} 
          eventId={eventId} 
          lectureId={lectureId} 
          onSaveLecture={onSaveLecture}
          onCloseRequest={onCloseRequest}
          isModal={isModal}
        />
      </ValidationProvider>
    </FormProvider>
  );
}